import { CEOQuery } from '../types';

export const CEO_QUERY_PATTERNS: CEOQuery[] = [
  // Financial Overview
  {
    pattern: 'daily revenue',
    sql: `
      SELECT 
        CAST(OrderDate as DATE) as Date,
        COUNT(*) as Orders,
        SUM(FinalGrandTotal) as Revenue,
        AVG(FinalGrandTotal) as AOV
      FROM Orders 
      WHERE OrderDate >= DATEADD(day, -30, GETDATE())
      GROUP BY CAST(OrderDate as DATE)
      ORDER BY Date DESC
    `,
    description: 'Daily revenue breakdown for the last 30 days',
    category: 'financial'
  },
  
  // Channel Performance
  {
    pattern: 'channel comparison',
    sql: `
      SELECT 
        CASE CartID
          WHEN 24 THEN 'Target'
          WHEN 2 THEN 'Amazon'
          WHEN 10 THEN 'Walmart'
          WHEN 11 THEN 'Direct'
          ELSE 'Other'
        END as Channel,
        COUNT(*) as Orders,
        SUM(FinalGrandTotal) as Revenue,
        AVG(FinalGrandTotal) as AOV,
        SUM(FinalGrandTotal) * 100.0 / SUM(SUM(FinalGrandTotal)) OVER () as RevenuePercent
      FROM Orders
      WHERE OrderDate >= DATEADD(month, -1, GETDATE())
      GROUP BY CartID
      ORDER BY Revenue DESC
    `,
    description: 'Channel performance comparison for the last month',
    category: 'financial'
  },
  
  // Problem Detection
  {
    pattern: 'problem orders',
    sql: `
      SELECT 
        o.OrderNumber,
        o.OrderDate,
        o.CustomerEmail,
        o.FinalGrandTotal,
        CASE 
          WHEN p.OrderNumber IS NULL AND t.OrderNumber IS NULL THEN 'Not Processed'
          WHEN p.OrderNumber IS NOT NULL AND t.OrderNumber IS NULL THEN 'Stuck in Packing'
          ELSE 'Other Issue'
        END as Issue
      FROM Orders o
      LEFT JOIN Packing p ON o.OrderNumber = p.OrderNumber
      LEFT JOIN Tracking t ON o.OrderNumber = t.OrderNumber
      WHERE o.approved = 1 
        AND o.OrderDate < DATEADD(day, -2, GETDATE())
        AND t.OrderNumber IS NULL
      ORDER BY o.OrderDate DESC
    `,
    description: 'Orders that are stuck or have processing issues',
    category: 'operational'
  },
  
  // Inventory Analysis
  {
    pattern: 'low stock alert',
    sql: `
      SELECT 
        SKU,
        ProductName,
        QOH as CurrentStock,
        ReorderPoint,
        (SELECT AVG(QuantityOrdered) 
         FROM [Order Details] od 
         WHERE od.SKU = i.SKU 
           AND od.OrderDate >= DATEADD(day, -30, GETDATE())
        ) as Avg30DaySales,
        CASE 
          WHEN QOH < ReorderPoint THEN 'CRITICAL'
          WHEN QOH < ReorderPoint * 2 THEN 'WARNING'
          ELSE 'OK'
        END as StockStatus
      FROM Inventory i
      WHERE QOH < ReorderPoint * 2
      ORDER BY StockStatus, QOH
    `,
    description: 'Products running low on stock',
    category: 'inventory'
  },

  // Top Products
  {
    pattern: 'top products',
    sql: `
      SELECT TOP 20
        p.SKU,
        p.ProductName,
        COUNT(od.OrderNumber) as Orders,
        SUM(od.QuantityOrdered) as UnitsSold,
        SUM(od.QuantityOrdered * od.UnitPrice) as Revenue,
        AVG(od.UnitPrice) as AvgPrice
      FROM Products p
      JOIN [Order Details] od ON p.SKU = od.SKU
      JOIN Orders o ON od.OrderNumber = o.OrderNumber
      WHERE o.OrderDate >= DATEADD(month, -1, GETDATE())
      GROUP BY p.SKU, p.ProductName
      ORDER BY Revenue DESC
    `,
    description: 'Top 20 products by revenue in the last month',
    category: 'financial'
  },

  // Customer Analysis
  {
    pattern: 'customer insights',
    sql: `
      SELECT 
        CustomerEmail,
        COUNT(*) as TotalOrders,
        SUM(FinalGrandTotal) as TotalSpent,
        AVG(FinalGrandTotal) as AvgOrderValue,
        MIN(OrderDate) as FirstOrder,
        MAX(OrderDate) as LastOrder,
        DATEDIFF(day, MAX(OrderDate), GETDATE()) as DaysSinceLastOrder
      FROM Orders
      WHERE OrderDate >= DATEADD(month, -6, GETDATE())
      GROUP BY CustomerEmail
      HAVING COUNT(*) > 1
      ORDER BY TotalSpent DESC
    `,
    description: 'Customer insights and behavior analysis',
    category: 'customer'
  },

  // Revenue Trends
  {
    pattern: 'revenue trends',
    sql: `
      SELECT 
        DATEPART(week, OrderDate) as WeekNumber,
        DATEPART(year, OrderDate) as Year,
        COUNT(*) as Orders,
        SUM(FinalGrandTotal) as Revenue,
        AVG(FinalGrandTotal) as AOV
      FROM Orders
      WHERE OrderDate >= DATEADD(month, -3, GETDATE())
      GROUP BY DATEPART(week, OrderDate), DATEPART(year, OrderDate)
      ORDER BY Year, WeekNumber
    `,
    description: 'Weekly revenue trends for the last 3 months',
    category: 'financial'
  },

  // Operational Issues
  {
    pattern: 'operational issues',
    sql: `
      SELECT 
        'Orders without tracking' as IssueType,
        COUNT(*) as Count
      FROM Orders o
      LEFT JOIN Tracking t ON o.OrderNumber = t.OrderNumber
      WHERE o.approved = 1 
        AND o.OrderDate < DATEADD(day, -1, GETDATE())
        AND t.OrderNumber IS NULL
      
      UNION ALL
      
      SELECT 
        'Orders stuck in packing' as IssueType,
        COUNT(*) as Count
      FROM Orders o
      JOIN Packing p ON o.OrderNumber = p.OrderNumber
      LEFT JOIN Tracking t ON o.OrderNumber = t.OrderNumber
      WHERE o.approved = 1 
        AND o.OrderDate < DATEADD(day, -2, GETDATE())
        AND t.OrderNumber IS NULL
    `,
    description: 'Current operational issues and bottlenecks',
    category: 'operational'
  }
];

export const QUERY_CATEGORIES = {
  financial: 'Financial Analysis',
  operational: 'Operational Issues',
  inventory: 'Inventory Management',
  customer: 'Customer Insights'
};

export function findMatchingQuery(userQuery: string): CEOQuery | null {
  const lowerQuery = userQuery.toLowerCase();
  
  // Check for exact pattern matches
  const exactMatch = CEO_QUERY_PATTERNS.find(query => 
    lowerQuery.includes(query.pattern.toLowerCase())
  );
  
  if (exactMatch) return exactMatch;
  
  // Check for keyword matches
  const keywords = {
    'revenue': CEO_QUERY_PATTERNS.filter(q => q.category === 'financial'),
    'orders': CEO_QUERY_PATTERNS.filter(q => q.category === 'operational'),
    'stock': CEO_QUERY_PATTERNS.filter(q => q.category === 'inventory'),
    'customer': CEO_QUERY_PATTERNS.filter(q => q.category === 'customer'),
    'problem': CEO_QUERY_PATTERNS.filter(q => q.pattern.includes('problem')),
    'top': CEO_QUERY_PATTERNS.filter(q => q.pattern.includes('top')),
    'trend': CEO_QUERY_PATTERNS.filter(q => q.pattern.includes('trend'))
  };
  
  for (const [keyword, queries] of Object.entries(keywords)) {
    if (lowerQuery.includes(keyword) && queries.length > 0) {
      return queries[0];
    }
  }
  
  return null;
}

export function generateSQLFromQuery(userQuery: string): string {
  const matchingQuery = findMatchingQuery(userQuery);
  
  if (matchingQuery) {
    return matchingQuery.sql;
  }
  
  // Default query for unknown patterns
  return `
    SELECT TOP 50
      o.OrderNumber,
      o.OrderDate,
      o.CustomerEmail,
      o.FinalGrandTotal,
      o.CartID
    FROM Orders o
    WHERE o.OrderDate >= DATEADD(day, -7, GETDATE())
    ORDER BY o.OrderDate DESC
  `;
} 